import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cx } from '../lib/cx';

export interface ModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly children: ReactNode;
  /** Pinned under the scrolling body, right-aligned — where the primary action goes. */
  readonly footer?: ReactNode;
  readonly className?: string;
}

/**
 * A centred dialog on Radix, so focus trapping, Escape and scroll lock come
 * for free instead of being half-reimplemented per screen.
 */
export function Modal({ open, onOpenChange, title, description, children, footer, className }: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/25" />
        <Dialog.Content
          className={cx(
            'fixed top-[6vh] left-1/2 z-50 flex max-h-[88vh] w-[calc(100vw-32px)] max-w-[33.75rem] -translate-x-1/2 flex-col',
            'rounded-xl border border-line bg-base shadow-[0_24px_64px_-16px_rgba(22,23,26,0.28)] focus:outline-none',
            className,
          )}
        >
          <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div className="min-w-0">
              <Dialog.Title className="fs-14 font-semibold text-ink">{title}</Dialog.Title>
              {description !== undefined ? (
                <Dialog.Description className="mt-0.5 fs-12 text-muted">{description}</Dialog.Description>
              ) : (
                <Dialog.Description className="sr-only">{title}</Dialog.Description>
              )}
            </div>
            <Dialog.Close
              aria-label="Close"
              className="-mt-1 -mr-2 rounded-md p-1.5 text-muted hover:bg-surface-2 hover:text-ink"
            >
              <X size={16} />
            </Dialog.Close>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

          {footer !== undefined && (
            <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">
              {footer}
            </footer>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
